/**
 * MessageToastLayer — 149-cw (PROMPT 16-CHAT-UX-NOTIFICATION-R2).
 *
 * Popup notifikasi pesan masuk yang tampil di kanan bawah DI ATAS tombol chat
 * FAB saat panel chat tertutup. Aturan:
 *   - Auto-dismiss 10s; timer di-pause saat cursor di atas popup.
 *   - Maksimal 3 popup tampil bersamaan; pesan lain dari sender sama digabung.
 *   - Klik "Balas" membuka drawer + auto-select percakapan (via onReply prop).
 *   - JANGAN tampil popup untuk:
 *      * pesan yang dikirim user sendiri
 *      * percakapan yang sedang terbuka (drawer open + selected == this conv)
 *      * kontak dimute (DND aktif)
 *      * duplikat dari reconnect / tab browser lain (via message.id dedup)
 *   - Suara lembut opt-in via `prefs.sound`.
 *   - Multi-tab: gunakan BroadcastChannel `ens_msg_dedup` untuk mark id yang
 *     sudah ditampilkan di salah satu tab.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { X, MessageSquare, Reply } from "lucide-react";
import { useAuth } from "@/lib/auth";
import useMessagingPrefs from "@/hooks/useMessagingPrefs";

const MAX_TOASTS = 3;
const AUTO_DISMISS_MS = 10_000;
const DEDUP_CHANNEL = "ens_msg_dedup_v1";

function useSoftBeep() {
  const ctxRef = useRef(null);
  const beep = () => {
    try {
      if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = ctxRef.current;
      if (ctx.state === "suspended") ctx.resume?.();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = 780;
      g.gain.value = 0.04;
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
      o.connect(g); g.connect(ctx.destination);
      o.start(); setTimeout(() => { o.stop(); }, 260);
    } catch { /* browser blocked — ignore */ }
  };
  return beep;
}

export default function MessageToastLayer({ drawerOpen, currentConversationId, onReply }) {
  const { user } = useAuth();
  const { prefs } = useMessagingPrefs(true);
  const [toasts, setToasts] = useState([]); // {id, conversation_id, sender_id, sender_name, sender_division, body, ts, count, paused}
  const seenIdsRef = useRef(new Set()); // dedup message.id (cross-tab via BroadcastChannel)
  const bcRef = useRef(null);
  const softBeep = useSoftBeep();

  // Cross-tab dedup channel
  useEffect(() => {
    try {
      bcRef.current = new BroadcastChannel(DEDUP_CHANNEL);
      bcRef.current.onmessage = (e) => {
        if (e?.data?.type === "seen" && e.data.msg_id) seenIdsRef.current.add(e.data.msg_id);
      };
    } catch { /* no-BC browser (rare) */ }
    return () => { try { bcRef.current?.close(); } catch { /* noop */ } };
  }, []);

  const markSeen = useCallback((msgId) => {
    seenIdsRef.current.add(msgId);
    try { bcRef.current?.postMessage({ type: "seen", msg_id: msgId }); } catch { /* noop */ }
  }, []);

  // Auto-dismiss ticker
  useEffect(() => {
    const t = setInterval(() => {
      setToasts((prev) => prev.filter((tst) => tst.paused || (Date.now() - tst.opened_at) < AUTO_DISMISS_MS));
    }, 500);
    return () => clearInterval(t);
  }, []);

  const dismiss = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  const enqueue = useCallback((detail) => {
    const p = detail || {};
    const m = p.message;
    if (!m || !m.id) return;
    // Rule: skip self.
    if (m.sender_id === user?.id) return;
    // Rule: skip already-seen (dedup across reconnect + tabs).
    if (seenIdsRef.current.has(m.id)) return;
    // Rule: skip if the same conversation is open in drawer.
    if (drawerOpen && p.conversation_id && p.conversation_id === currentConversationId) {
      markSeen(m.id);
      return;
    }
    // Rule: DND — skip toast (badge counter di FAB tetap update via MessengerFAB).
    if (prefs?.dnd) return;
    markSeen(m.id);

    setToasts((prev) => {
      // Group same-sender toast: bump count + refresh timer + newest preview.
      const existingIdx = prev.findIndex((t) => t.sender_id === m.sender_id && t.conversation_id === p.conversation_id);
      if (existingIdx >= 0) {
        const cloned = [...prev];
        cloned[existingIdx] = {
          ...cloned[existingIdx],
          body: m.body || cloned[existingIdx].body,
          count: (cloned[existingIdx].count || 1) + 1,
          opened_at: Date.now(),
          paused: false,
        };
        return cloned;
      }
      // Cap at MAX_TOASTS — drop oldest.
      const next = [
        {
          id: m.id,
          conversation_id: p.conversation_id,
          sender_id: m.sender_id,
          sender_name: p.sender_name || m.sender_name || "Pengirim",
          sender_division: p.sender_division || m.sender_division || "",
          body: m.body || "(pesan)",
          count: 1,
          opened_at: Date.now(),
          paused: false,
        },
        ...prev,
      ].slice(0, MAX_TOASTS);
      return next;
    });

    if (prefs?.sound) softBeep();
  }, [user?.id, drawerOpen, currentConversationId, prefs, softBeep, markSeen]);

  useEffect(() => {
    const onWs = (e) => {
      const p = e.detail || {};
      if (p.type === "message_new") enqueue(p);
    };
    const onMsg = (e) => {
      const p = e.detail || {};
      if (p.type === "message_new") enqueue(p);
    };
    window.addEventListener("ens:ws", onWs);
    window.addEventListener("ens:msg", onMsg);
    return () => {
      window.removeEventListener("ens:ws", onWs);
      window.removeEventListener("ens:msg", onMsg);
    };
  }, [enqueue]);

  // Auto-dismiss any toast whose conversation becomes open.
  useEffect(() => {
    if (drawerOpen && currentConversationId) {
      setToasts((prev) => prev.filter((t) => t.conversation_id !== currentConversationId));
    }
  }, [drawerOpen, currentConversationId]);

  if (!user) return null;
  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed z-30 pointer-events-none flex flex-col-reverse gap-2"
      style={{ right: 20, bottom: 80 /* di atas FAB (bottom-5 + 12x12 = ~68) */ }}
      data-testid="msg-toast-layer"
    >
      {toasts.map((t) => (
        <ToastCard
          key={t.id}
          toast={t}
          onDismiss={() => dismiss(t.id)}
          onReply={() => { onReply?.(t.conversation_id); dismiss(t.id); }}
          onPause={(paused) => setToasts((prev) => prev.map((x) => (x.id === t.id ? { ...x, paused, opened_at: paused ? x.opened_at : Date.now() } : x)))}
        />
      ))}
    </div>
  );
}

function ToastCard({ toast, onDismiss, onReply, onPause }) {
  const initials = ((toast.sender_name || "?").split(" ").slice(0, 2).map((s) => s[0]).join("")).toUpperCase();
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-testid={`msg-toast-${toast.id}`}
      className="pointer-events-auto w-[340px] max-w-[calc(100vw-40px)] bg-white border-l-4 border-[#FF5A1F] shadow-2xl overflow-hidden"
      onMouseEnter={() => onPause(true)}
      onMouseLeave={() => onPause(false)}
    >
      <div className="p-3 flex items-start gap-2.5">
        <div className="relative shrink-0">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF5A1F] to-[#e04a13] text-white font-bold text-xs flex items-center justify-center">
            {initials}
          </div>
          <MessageSquare size={12} className="absolute -bottom-1 -right-1 bg-[#0B1D3A] text-white rounded-full p-0.5 border-2 border-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-[#FF5A1F] font-semibold">
              Pesan baru dari
            </span>
            {toast.count > 1 && (
              <span className="text-[9px] tabular-nums bg-[#FF5A1F] text-white px-1 py-0.5 rounded-full" data-testid={`msg-toast-count-${toast.id}`}>
                {toast.count}
              </span>
            )}
          </div>
          <div className="text-[13px] font-semibold text-[#0B1D3A] truncate" data-testid={`msg-toast-sender-${toast.id}`}>
            {toast.sender_name}
            {toast.sender_division && <span className="ml-1 text-[10px] font-normal text-slate-500">· {toast.sender_division}</span>}
          </div>
          <div className="text-xs text-slate-700 mt-0.5 line-clamp-2 leading-snug" data-testid={`msg-toast-body-${toast.id}`}>
            {toast.body}
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Tutup notifikasi"
          title="Tutup"
          data-testid={`msg-toast-close-${toast.id}`}
          className="text-slate-400 hover:text-slate-700 shrink-0 -mr-1 -mt-1 p-1"
        >
          <X size={14} />
        </button>
      </div>
      <div className="border-t border-slate-100 flex">
        <button
          type="button"
          onClick={onReply}
          data-testid={`msg-toast-reply-${toast.id}`}
          className="flex-1 px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-[#FF5A1F] hover:bg-orange-50 flex items-center justify-center gap-1.5"
        >
          <Reply size={12} /> Balas
        </button>
        <button
          type="button"
          onClick={onDismiss}
          data-testid={`msg-toast-tutup-${toast.id}`}
          className="px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500 hover:bg-slate-50 border-l border-slate-100"
        >
          Tutup
        </button>
      </div>
    </div>
  );
}
